/// <amd-module name="Artifacts/Package/redux/Sagas" />
import {
  IFollowActionPayload,
  IIngestFromUpstreamPayload,
  ILoadPackageDetailsPayload,
  ILoadUpstreamVersionsPayload,
  IUpstreamOverrideModalStartPayload,
  PackageActions,
  PackageActionTypes,
} from "Artifacts/Package/redux/Actions";
import * as PackageRes from "Artifacts/Package/Resources";
import * as Res from "Artifacts/Package/Resources";
import { IFeedFollowsDataService } from "Artifacts/Package/services/FollowsDataService";
import { IPackageService } from "Artifacts/Package/services/PackageService";
import { IUpstreamVersionsService } from "Artifacts/Package/services/UpstreamVersionsService";
import { getSelectedFeed } from "Artifacts/Root/redux/Selectors";
import { call, getContext, put, select, takeEvery } from "Artifacts/Root/Saga";
import { IFeed } from "Artifacts/Root/types/IFeed";
import { effects, SagaIterator } from "redux-saga";
import { ActionWithPayload } from "redux/Utils/ActionHelper";
import { RT } from "redux/Utils/SagaHelper";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService.Types";
import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

export function* rootSaga(): SagaIterator {
  yield effects.takeLeading(PackageActionTypes.LoadPackageDetails, initializePackageDetails);
  yield effects.takeLeading(PackageActionTypes.LoadUpstreamVersions, initializeUpstreamVersions);
  yield takeEvery(PackageActionTypes.GetFollowState, getFollowState);
  yield takeEvery(PackageActionTypes.SetFollowState, setFollowState);
  yield takeEvery(PackageActionTypes.UpstreamOverrideModalStarted, getOverrideState);
  yield effects.takeLeading(PackageActionTypes.IngestFromUpstream, ingestFromUpstream);
}

export function* initializePackageDetails(
  action: ActionWithPayload<PackageActionTypes.LoadPackageDetails, ILoadPackageDetailsPayload>,
): SagaIterator {
  try {
    const {
      feedId,
      protocol,
      pkgName,
      onSuccessCallback,
      version,
      viewId,
      projectName,
      preferRelease,
    } = action.payload;
    announce(Utils_String.format(Res.PackageDetails_AnnounceLoadStart, pkgName));

    const context: IVssPageContext = (yield getContext("pageContext")) as any;
    const packageService: IPackageService = context.getService("IPackageService");

    const data: RT<typeof packageService.getPackageDetailsData> = (yield call(
      [packageService, packageService.getPackageDetailsData],
      context,
      feedId,
      protocol,
      pkgName,
      version,
      viewId,
      projectName,
      preferRelease,
    )) as any;

    announce(Res.PackageDetails_AnnounceLoadSuccess);
    yield put(
      PackageActions.loadPackageDetailsCompleted(data.package, data.currentVersion, data.versions),
    );
    onSuccessCallback(data.package.protocol, data.package.name, data.currentVersion.version);
  } catch (error) {
    announce(Res.PackageDetails_AnnounceLoadError);
    yield put(PackageActions.loadPackageDetailsFailed(error.message));
  }
}

export function* initializeUpstreamVersions(
  action: ActionWithPayload<PackageActionTypes.LoadUpstreamVersions, ILoadUpstreamVersionsPayload>,
): SagaIterator {
  try {
    const { feedId, protocol, pkgName, projectId } = action.payload;
    announce(Utils_String.format(Res.UpstreamVersions_AnnounceLoadStart, pkgName));

    const context: IVssPageContext = (yield getContext("pageContext")) as any;
    const upstreamVersionsService: IUpstreamVersionsService = context.getService(
      "IUpstreamVersionsService",
    );

    const data: RT<typeof upstreamVersionsService.getUpstreamVersionsData> = (yield call(
      [upstreamVersionsService, upstreamVersionsService.getUpstreamVersionsData],
      context,
      feedId,
      protocol,
      pkgName,
      projectId,
    )) as any;

    announce(Res.UpstreamVersions_AnnounceLoadSuccess);
    yield put(PackageActions.loadUpstreamVersionsCompleted(data.upstreamVersionsData));
  } catch (error) {
    announce(Res.UpstreamVersions_AnnounceLoadError);
    yield put(PackageActions.loadUpstreamVersionsFailed(error.message));
  }
}

export function* getFollowState(
  action: ActionWithPayload<PackageActionTypes.GetFollowState, IFollowActionPayload>,
): SagaIterator {
  try {
    const { feed, packageToFollow } = action.payload;

    const context: IVssPageContext = (yield getContext("pageContext")) as any;
    const service: IFeedFollowsDataService = context.getService("IFeedFollowsDataService");

    const data: boolean = ((yield call(
      [service, service.getFollowState],
      packageToFollow,
      feed,
    )) as unknown) as boolean;

    yield put(PackageActions.getFollowStateCompleted(data));
  } catch (error) {
    // Do nothing - it will default to false.
  }
}

export function* setFollowState(
  action: ActionWithPayload<PackageActionTypes.SetFollowState, IFollowActionPayload>,
): SagaIterator {
  try {
    const { feed, packageToFollow, state } = action.payload;

    if (state === undefined) {
      throw new Error("State should never be undefined");
    }

    const context: IVssPageContext = (yield getContext("pageContext")) as any;
    const service: IFeedFollowsDataService = context.getService("IFeedFollowsDataService");

    if (state) {
      // In the case where follow-state was not retrieved initially, we avoid creating a subscription if one already exists.
      yield call([service, service.follow], packageToFollow, feed);
      announce(Utils_String.format(PackageRes.Following_Announcement, packageToFollow.name));
    } else {
      yield call([service, service.unfollow], packageToFollow, feed);
      announce(Utils_String.format(PackageRes.Unfollowing_Announcement, packageToFollow.name));
    }

    yield put(PackageActions.getFollowStateCompleted(state));
  } catch (error) {
    const { packageToFollow, state } = action.payload;
    const context: IVssPageContext = (yield getContext("pageContext")) as any;
    const messageService = context.getService<IGlobalMessagesService>("IGlobalMessagesService");

    if (state) {
      messageService.addToast({
        message: Utils_String.format(PackageRes.Follow_Error, packageToFollow.name),
        duration: 4000,
      });
    } else {
      messageService.addToast({
        message: Utils_String.format(PackageRes.Unfollow_Error, packageToFollow.name),
        duration: 4000,
      });
    }
  }
}

export function* getOverrideState(
  action: ActionWithPayload<
    PackageActionTypes.UpstreamOverrideModalStarted,
    IUpstreamOverrideModalStartPayload
  >,
): SagaIterator {
  try {
    const { protocol, packageName } = action.payload;

    const feed: IFeed | undefined = yield select(getSelectedFeed);
    if (!feed) {
      throw new Error("Failed to retrieve feed from redux store");
    }

    const context: IVssPageContext = (yield getContext("pageContext")) as any;

    const overrideStatus: RT<typeof protocol.getUpstreamOverrideStatus> = (yield call(
      [protocol, protocol.getUpstreamOverrideStatus],
      context,
      packageName,
      feed,
    )) as any;

    yield put(PackageActions.upstreamOverrideModalCompleted(overrideStatus));
  } catch (error) {
    yield put(PackageActions.upstreamOverrideModalFailed(error.message));
  }
}

export function* ingestFromUpstream(
  action: ActionWithPayload<
    PackageActionTypes.IngestFromUpstreamStarted,
    IIngestFromUpstreamPayload
  >,
): SagaIterator {
  try {
    const { onSuccess, feedId, protocolName, pkgName, version, projectName } = action.payload;
    announce(Utils_String.format(Res.PackageIngestion_AnnounceLoadStart, pkgName, version));

    const context: IVssPageContext = (yield getContext("pageContext")) as any;
    const packageService: IPackageService = context.getService("IPackageService");

    yield put(PackageActions.upstreamIngestionStarted());

    const data: RT<typeof packageService.ingestFromUpstream> = (yield call(
      [packageService, packageService.ingestFromUpstream],
      { ingestFromUpstream: true },
      protocolName,
      feedId,
      pkgName,
      version,
      projectName,
    )) as any;

    announce(Res.PackageIngestion_AnnounceLoadSuccess);
    yield put(PackageActions.upstreamIngestionCompleted(version));
    yield call(onSuccess);
  } catch (error) {
    const { onSuccess, feedId, protocolName, pkgName, version, projectName } = action.payload;
    announce(Utils_String.format(Res.PackageIngestion_AnnounceLoadError, pkgName, version));
    yield put(PackageActions.upstreamIngestionFailed(error.message));
  }
}
